import { PlusIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ClassNamesConfig, GroupBase } from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { createClassNames } from "@/components/ui/react-select";
import { getRecordingQueryKey } from "@/features/recordings/api/get-recording";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { PublicRecording } from "@classview/shared-types";

import { useCreateRecordingTag } from "../api/add-recording-tag";
import { useFetchRecordingTags } from "../api/fetch-recording-tags";
import { AddTagFormData } from "../types/add-tag-schema";
import { TagOption } from "../types/tag-option";

type AddTagFormProps = {
  recording: PublicRecording;
};

export function AddTagForm({ recording }: AddTagFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string>("");
  const form = useForm<AddTagFormData>({
    defaultValues: {
      tag: {
        value: "",
        label: "",
      },
    },
  });

  const {
    mutateAsync: addTagMutation,
    isPending: isAddTagPending,
    isError: isAddTagError,
    reset: resetAddTag,
  } = useCreateRecordingTag(recording.id, {
    onSuccess: (response) => {
      form.reset();
      setIsOpen(false);
      queryClient.setQueryData(
        getRecordingQueryKey(organisation.id, recording.id),
        (oldData: PublicRecording) => {
          return {
            ...oldData,
            tags: [...oldData.tags, response],
          };
        },
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { organisation } = useOrganisation();
  const formRef = useRef<HTMLFormElement>(null);

  const { data: tags, isLoading: isTagsLoading } = useFetchRecordingTags(
    {
      organisationId: organisation.id,
    },
    {
      select: (data) => {
        return data.filter(
          (tag) => !recording.tags.some((t) => t.id === tag.id),
        );
      },
    },
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  });

  useEffect(() => {
    form.reset({
      tag: {
        value: "",
        label: "",
      },
    });
    resetAddTag();
  }, [isOpen, form, resetAddTag]);

  const addTag = (tag: TagOption) => {
    form.setValue("tag", tag);
    form.trigger("tag");
    form.handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: AddTagFormData) => {
    const isNewTag = data.tag.value === data.tag.label;
    await addTagMutation({
      name: data.tag.label,
      tagId: isNewTag ? undefined : data.tag.value,
    });
  };

  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn("w-56", isAddTagError && "border-destructive");
    },
  });

  if (!isOpen) {
    return (
      <Button onClick={() => setIsOpen(true)} type="button" variant="ghost">
        <PlusIcon className="mr-2 h-4 w-4" />
        Add Tag
      </Button>
    );
  }

  return (
    <Form {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
        <CreatableSelect<TagOption>
          unstyled
          autoFocus
          components={{
            DropdownIndicator: () => null,
            ClearIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          classNames={
            reactSelectClassNames as ClassNamesConfig<
              TagOption,
              false,
              GroupBase<TagOption>
            >
          }
          isLoading={isTagsLoading || isAddTagPending}
          options={tags?.map((tag) => ({
            value: tag.id,
            label: tag.name,
          }))}
          placeholder="Type tag name..."
          formatCreateLabel={(input) => `Create new tag: "${input}"`}
          isValidNewOption={(inputValue: string) => {
            return inputValue.length >= 2;
          }}
          noOptionsMessage={() => "Type tag name..."}
          isDisabled={form.formState.isSubmitting || isTagsLoading}
          inputValue={value}
          onInputChange={(value) => {
            setValue(value);
          }}
          onChange={(value) => {
            if (value) {
              addTag(value);
              setValue(""); // Clear the input once added
              return null; // Prevent adding to internal state
            }
          }}
        />
      </form>
    </Form>
  );
}
